// Pair API — typed wrappers for /api/v1/pairs/* + /api/v1/pair-invites/*.
//
// Mirrors the W2 §6.2 backend surface (PATCH pair, dissolve, list-invites,
// cancel-invite, public-invite read, accept with optional pair_name +
// relationship_start).
//
// Types are aliases onto the generated contract where they are identical to it
// (`schema.ts`); the rest are still hand-written, each with a recorded reason in
// `schema-parity.test.ts`.

import { api as openapi } from "./client";
import { assertResponseBody } from "./emptyBody";
import type {
  AcceptInviteRequest as SchemaAcceptInviteRequest,
  DissolveResponse as SchemaDissolveResponse,
  PairOut as SchemaPairOut,
  PatchPairRequest as SchemaPatchPairRequest,
} from "./schema";
import { API_REQUEST_FAILED } from "../i18n/errors";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = Record<"GET" | "POST" | "PUT" | "DELETE" | "PATCH", any>;
const api = openapi as unknown as AnyClient;

interface CallOptions {
  body?: unknown;
  headers?: Record<string, string>;
  /** openapi-fetch reads query params from `params.query` — see diary.ts. */
  params?: { query: Record<string, unknown> };
  /**
   * Set `false` only on a route whose OpenAPI contract declares no 2xx body
   * (every one of them answers `204`). A 2xx that arrives empty on any other
   * route is a broken server and must reach `isError` — see `emptyBody.ts`.
   */
  expectBody?: boolean;
}

async function call<T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  path: string,
  opts: CallOptions = {},
): Promise<{ response: Response; data: T | null; error?: unknown }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await (api as any)[method](path, opts);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = (result.data as any) ?? null;
  // A bodyless 2xx where the contract declares a body arrives as `null` and
  // every `return res.data as T` below would ship it to a screen. Guard the
  // one funnel rather than each component — see `emptyBody.ts`.
  if (result.response?.ok)
    assertResponseBody(result.response, result.data, opts.expectBody !== false);
  // W39.79. `error` is openapi-fetch's **already-parsed** body for a non-2xx.
  // Without it `apiError` can only try to re-read a body the client consumed.
  return { response: result.response, data, error: (result as { error?: unknown }).error };
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type PairOut = SchemaPairOut;

/** Hand-written: the contract types `role` as bare `string`; the UI switches on `"owner" | "partner"`. */
export interface PairMemberOut {
  pair_id: string;
  user_id: string;
  role: "owner" | "partner";
  joined_at: string;
}

/**
 * 刻意手寫：contract 的 `PairResponse.members` 是 `role: string` 的
 * `PairMemberOut`，但 UI 要用 `"owner" | "partner"` 才有辦法 switch。
 * alias 外層會讓它跟下面兩行宣告的 `PairMemberOut` 互相矛盾。
 * 見 `schema-parity.test.ts`。
 */
export interface PairResponse {
  pair: PairOut;
  members: PairMemberOut[];
}

export interface PairInviteOut {
  id: string;
  pair_id: string;
  code: string;
  expires_at: string;
  accepted_by: string | null;
  accepted_at: string | null;
  created_at: string;
}

/** W2 §6.2: QR format is SVG (vector); W1 used base64 PNG. */
export interface PairInviteResponse {
  invite: PairInviteOut;
  qr_svg: string;
}

/**
 * 刻意手寫：contract 把 `accepted_at` / `accepted_by` 標成 optional，而
 * 邀請列表兩者都直接 render、沒有做 presence check。與上面的 `PairResponse`
 * 是同一種「外層與元素互相矛盾」的情況。
 */
export interface ListPairInvitesResponse {
  items: PairInviteOut[];
}

export type PublicInviteStatus = "pending" | "accepted" | "cancelled" | "expired";

export interface PublicInviteResponse {
  code: string;
  status: PublicInviteStatus;
  /** Host display_name; surfaced ONLY when status === "pending". */
  inviter_display_name: string | null;
  expires_at: string | null;
  accepted_at: string | null;
  pair_id: string | null;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type DissolveResponse = SchemaDissolveResponse;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type AcceptInvitePayload = SchemaAcceptInviteRequest;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type PatchPairPayload = SchemaPatchPairRequest;

export interface ApiErrorShape {
  status: number;
  code: string;
  message: string;
  details?: { field: string; reason: string; message: string }[];
}

export class ApiError extends Error {
  status: number;
  code: string;
  details?: ApiErrorShape["details"];
  retry_after_seconds?: number;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: ApiErrorShape["details"],
    retry_after_seconds?: number,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.retry_after_seconds = retry_after_seconds;
  }
}

/**
 * Decode the server's error envelope into an {@link ApiError}.
 *
 * W39.79. This body, plus the `ApiErrorShape` interface and the `ApiError` class
 * above, was **verbatim duplicated** in `lib/api/auth.ts` — three copies, none
 * importing the others. Exported now so there is one.
 *
 * The duplication was not cosmetic. Pages branch on `err instanceof ApiError`, so
 * a page holding one class cannot recognise an error built from the other: the
 * `instanceof` is false, no branch throws, and the page quietly takes its generic
 * path. That is how the change-password form ended up rendering one line reading
 * 「更改密碼失敗」 for every failure it can produce — and how a test written
 * against this class reported a *different* reason than the one it was chasing,
 * because the page was catching the other copy.
 */
export async function apiError(
  res: { response: Response; data: unknown; error?: unknown },
  fallbackCode: string,
): Promise<ApiError> {
  // W39.79. `res.error` first, and only then the response body.
  //
  // `openapi-fetch` **parses the body itself** — `node_modules/openapi-fetch/dist/
  // index.js:244` is `let error = await response.text();` — and hands the parsed
  // value back as `result.error`. So the `Response` in `res.response` arrives with
  // its body already consumed, `clone().json()` cannot read it, and this function
  // quietly fell through to `API_REQUEST_FAILED` for **every** non-2xx response in
  // the app.
  //
  // Measured: a wrong current password answers 401 with an 82-byte envelope
  // (`AUTH_INVALID_CREDENTIALS` / 「帳號或密碼不正確」) and the page rendered
  // 「請求未能完成，請稍後再試。」 — the server's own words, discarded. The
  // W39.7 pass made this function's *fallback* Chinese; it never made it read the
  // body, so nothing downstream could tell the difference.
  //
  // The response is still tried, because a caller may hand over a `Response` it
  // built itself without going through `openapi-fetch`.
  let body: unknown = res.error ?? null;
  if (body === null) {
    try {
      body = await res.response.clone().json();
    } catch {
      body = null;
    }
  }
  const err = (
    body as {
      error?: { code?: string; message?: string; details?: ApiErrorShape["details"] };
    } | null
  )?.error;
  const retryAfter = Number(res.response.headers.get("Retry-After")) || undefined;
  return new ApiError(
    res.response.status,
    err?.code ?? fallbackCode,
    err?.message ?? API_REQUEST_FAILED,
    err?.details,
    retryAfter,
  );
}

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

/** GET /api/v1/pairs/{pair_id} — read current Pair (member only). */
export async function readPair(pairId: string): Promise<PairResponse> {
  const res = await call<PairResponse>("GET", `/api/v1/pairs/${pairId}`);
  if (!res.response.ok) throw await apiError(res, "PAIR_READ_FAILED");
  return res.data as PairResponse;
}

/**
 * POST /api/v1/pairs/invite — generate 6-digit code + SVG QR.
 *
 * Per W2 §4.3 + ADR-0013: pass `couple_name` + `relationship_start` so the
 * user-confirmed values land on the auto-created Pair.  Both are optional;
 * omit to fall back to the server default ("我們" + today).
 */
export async function createInvite(payload?: {
  couple_name?: string;
  relationship_start?: string; // YYYY-MM-DD
}): Promise<PairInviteResponse> {
  const body = payload && (payload.couple_name || payload.relationship_start) ? payload : undefined;
  const res = await call<PairInviteResponse>("POST", "/api/v1/pairs/invite", { body });
  if (!res.response.ok) throw await apiError(res, "PAIR_INVITE_FAILED");
  return res.data as PairInviteResponse;
}

/** POST /api/v1/pairs/accept — redeem an invite code (SAVEPOINT race-safe). */
export async function acceptInvite(payload: AcceptInvitePayload): Promise<PairResponse> {
  const res = await call<PairResponse>("POST", "/api/v1/pairs/accept", {
    body: payload,
  });
  if (!res.response.ok) throw await apiError(res, "PAIR_ACCEPT_FAILED");
  return res.data as PairResponse;
}

/** PATCH /api/v1/pairs/{pair_id} — edit name / relationship_start. */
export async function patchPair(pairId: string, payload: PatchPairPayload): Promise<PairResponse> {
  const res = await call<PairResponse>("PATCH", `/api/v1/pairs/${pairId}`, {
    body: payload,
  });
  if (!res.response.ok) throw await apiError(res, "PAIR_PATCH_FAILED");
  return res.data as PairResponse;
}

/** POST /api/v1/pairs/{pair_id}/dissolve — soft-delete; emits pair.dissolved. */
export async function dissolvePair(pairId: string): Promise<DissolveResponse> {
  const res = await call<DissolveResponse>("POST", `/api/v1/pairs/${pairId}/dissolve`);
  if (!res.response.ok) throw await apiError(res, "PAIR_DISSOLVE_FAILED");
  return res.data as DissolveResponse;
}

/** GET /api/v1/pairs/{pair_id}/invites — list active (pending) invites. */
export async function listPairInvites(pairId: string): Promise<ListPairInvitesResponse> {
  const res = await call<ListPairInvitesResponse>("GET", `/api/v1/pairs/${pairId}/invites`);
  if (!res.response.ok) throw await apiError(res, "PAIR_INVITES_LIST_FAILED");
  return res.data as ListPairInvitesResponse;
}

/** DELETE /api/v1/pair-invites/{id} — cancel invite (creator-pair member only). */
export async function cancelInvite(inviteId: string): Promise<void> {
  const res = await call<unknown>("DELETE", `/api/v1/pair-invites/${inviteId}`, {
    expectBody: false,
  });
  if (!res.response.ok) throw await apiError(res, "PAIR_INVITE_CANCEL_FAILED");
}

/** GET /api/v1/pair-invites/{code}/public — anonymous-safe single invite read. */
export async function readPublicInvite(code: string): Promise<PublicInviteResponse> {
  const res = await call<PublicInviteResponse>("GET", `/api/v1/pair-invites/${code}/public`);
  if (!res.response.ok) throw await apiError(res, "PAIR_INVITE_LOOKUP_FAILED");
  return res.data as PublicInviteResponse;
}
